import { useQuery } from "@tanstack/react-query";
import {
  CalendarClock,
  CheckCircle2,
  CreditCard,
  FileText,
  GraduationCap,
  Megaphone,
  Users,
} from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { web } from "@/config/System";
import { getLinimasa } from "@/services/getData";
import { Periode } from "@/types";

// --- HELPER FUNCTIONS ---
export const formatDateToIndonesian = (date: string | Date | undefined) => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(d);
};

export const formatDateTimeToIndonesian = (date: string | Date | undefined) => {
  if (!date) return "-";
  const d = new Date(date);
  if (isNaN(d.getTime())) return "-";

  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
};

export const formatDateRange = (start?: string, end?: string) => {
  if (!start) return "-";
  const startDate = formatDateToIndonesian(start);
  if (!end) return startDate;
  const endDate = formatDateToIndonesian(end);
  if (startDate === endDate) return startDate;
  return `${startDate} - ${endDate}`;
};

// --- HELPER COMPONENTS ---

// 2. Komponen Baris Timeline (Reusable)
const TimelineRow = ({
  icon: Icon,
  label,
  date,
  isLast = false,
}: {
  icon: any;
  label: string;
  date: string;
  isLast?: boolean;
}) => (
  <div className="relative flex gap-4 pb-6 last:pb-0">
    {/* Garis vertikal timeline */}
    {!isLast && (
      <span className="absolute left-[11px] top-8 h-full w-[2px] bg-slate-100 dark:bg-slate-800" />
    )}

    {/* Icon Wrapper */}
    <div className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/30 ring-4 ring-white dark:ring-slate-900">
      <Icon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
    </div>

    {/* Content */}
    <div className="flex flex-col gap-1">
      <span className="text-sm font-medium text-slate-900 dark:text-slate-200 leading-none">
        {label}
      </span>
      <span className="text-sm text-slate-500 dark:text-slate-400">{date}</span>
    </div>
  </div>
);

// 3. Komponen Kartu Jadwal (Logic Extractor)
const ScheduleCard = ({ item }: { item: Periode }) => {
  const type = item.jalur_jenis?.nama_jalur_jenis_slug;
  const isNasional = item.jalur_jenis?.jalur?.nama_jalur.startsWith("Nasional");
  const isCbt = type === "cbt";
  const isTalent = type === "talentscouting";
  const isPasca = type === "pascasarjana";
  const isPrestasi = type === "prestasi";

  const schedules = useMemo(() => {
    const list = [];

    if (!isNasional) {
      list.push({
        icon: FileText,
        label: "Masa Pendaftaran",
        date: formatDateRange(
          item.waktu_mulai_daftar,
          item.waktu_selesai_daftar,
        ),
      });
    }

    if (isCbt) {
      list.push({
        icon: CalendarClock,
        label: "Pelaksanaan Ujian CBT",
        date:
          item.tanggal_cbt?.map((t) => formatDateToIndonesian(t)).join(", ") ||
          "-",
      });
    }
    if (isTalent) {
      list.push({
        icon: Users,
        label: "Pelaksanaan Wawancara",
        date:
          item.tanggal_cbt?.map((t) => formatDateToIndonesian(t)).join(", ") ||
          "-",
      });
    }
    if (isPasca) {
      if (item.waktu_tpa_pasca) {
        list.push({
          icon: FileText,
          label: "Tes Potensi Akademik (TPA)",
          date: formatDateTimeToIndonesian(item.waktu_tpa_pasca) + " Wita",
        });
      }
      if (item.waktu_tkb_pasca) {
        list.push({
          icon: FileText,
          label: "Tes Kompetensi Bidang (TKB)",
          date: formatDateTimeToIndonesian(item.waktu_tkb_pasca) + " Wita",
        });
      }
    }
    if (!isNasional) {
      list.push({
        icon: Megaphone,
        label: !isPasca ? "Pengumuman Kelulusan" : "Pengumuman Kelulusan & BKT",
        date: formatDateRange(item.waktu_pengumuman_kelulusan),
      });
    }

    if (!isPasca) {
      list.push({
        icon: FileText,
        label: "Pengisian Data UKT",
        date: formatDateRange(
          item.waktu_mulai_isi_ukt,
          item.waktu_selesai_isi_ukt,
        ),
      });
    }
    if (!isPasca) {
      list.push({
        icon: Megaphone,
        label: "Pengumuman UKT/KIP-K",
        date: formatDateRange(item.waktu_pengumuman),
      });
    }
    // list.push({
    //   icon: Megaphone,
    //   label: isNasional ? "Pengumuman UKT/KIP-K" : "Pengumuman Kelulusan",
    //   date: formatDateRange(
    //     isNasional ? item.waktu_pengumuman : item.waktu_pengumuman_kelulusan,
    //   ),
    // });

    //if (!isCbt && !isPrestasi && !isTalent) {
    list.push({
      icon: CreditCard,
      label: !isPasca ?"Pembayaran UKT" : "Pembayaran BKT",
      date: formatDateRange(
        item.waktu_mulai_bayar_ukt,
        item.waktu_selesai_bayar_ukt,
      ),
    });
    //}

    list.push({
      icon: CheckCircle2,
      label: "Daftar Kembali (Cetak NIM)",
      date: formatDateRange(
        item.waktu_mulai_daftarkembali,
        item.waktu_selesai_daftarkembali,
      ),
    });

    return list;
  }, [item, isNasional, isCbt, isTalent, isPasca, isPrestasi]);

  return (
    <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm hover:border-blue-200 dark:hover:border-blue-700 transition-all duration-200">
      <CardHeader className="pb-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
        <div className="flex flex-wrap items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base font-bold text-slate-800 dark:text-slate-100">
              {item.jalur_jenis?.nama_jalur_jenis}
              {item.jalur_jenis?.nama_jalur_jenis_slug === "pascasarjana"
                ? item.is_internasional_kerjasama
                  ? "(KERJASAMA)"
                  : "(REGULER)"
                : ""}
            </CardTitle>
            <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-medium">
              <GraduationCap className="w-3.5 h-3.5" />
              Tahun {item.tahun}
            </div>
          </div>

          {item.gelombang_show && (
            <Badge
              variant="outline"
              className="bg-white dark:bg-slate-800 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300"
            >
              Gelombang {item.gelombang_show}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="flex flex-col">
          {schedules.map((sch, idx) => (
            <TimelineRow
              key={idx}
              icon={sch.icon}
              label={sch.label}
              date={sch.date}
              isLast={idx === schedules.length - 1}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// --- MAIN COMPONENT ---

export default function InformasiModern() {
  const { data: linimasa, isPending } = useQuery({
    queryKey: ["linimasa"],
    queryFn: getLinimasa,
  });

  return (
    <section
      id="informasi-terkini"
      className="py-20 min-h- dark:bg-slate-950/50 transition-colors duration-300"
    >
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header Section */}
        <div className="text-center mb-16 space-y-4">
          <Badge
            variant="secondary"
            className="mb-2 bg-blue-100 text-blue-700 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
          >
            Timeline Kegiatan
          </Badge>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
            Jadwal Seleksi Masuk 2026
          </h1>
          <p className="text-slate-500 dark:text-slate-400 max-w-xl mx-auto text-lg">
            Pantau terus tanggal-tanggal penting agar Anda tidak melewatkan
            tahapan seleksi di {web.univ}.
          </p>
        </div>

        {/* Content Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left Column: Timeline / Accordion */}
          <div className="lg:col-span-8 space-y-6">
            {isPending ? (
              // Skeleton Loading
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Skeleton
                    key={i}
                    className="h-24 w-full rounded-xl bg-slate-200 dark:bg-slate-800"
                  />
                ))}
              </div>
            ) : linimasa ? (
              <Accordion
                type="single"
                collapsible
                className="w-full space-y-4"
                defaultValue={Object.keys(linimasa)[0]}
              >
                {Object.keys(linimasa).map((key, index) => (
                  <AccordionItem
                    key={index}
                    value={key}
                    className="border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900 shadow-sm overflow-hidden"
                  >
                    <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <span className="text-lg font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                        <div className="w-2 h-8 bg-blue-600 dark:bg-blue-500 rounded-full mr-2" />
                        {key}
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="px-6 pb-6 pt-2 bg-slate-50/30 dark:bg-slate-950/30 border-t border-slate-100 dark:border-slate-800">
                      <div className="grid grid-cols-1 gap-4 mt-4">
                        {linimasa[key]?.map((item: Periode, idx: number) => (
                          <ScheduleCard key={idx} item={item} />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="text-center py-20 bg-white dark:bg-slate-900 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                <p className="text-slate-500 dark:text-slate-400">
                  Belum ada jadwal yang tersedia.
                </p>
              </div>
            )}
          </div>

          {/* Right Column: Sticky Sidebar / Action Buttons */}
          <div className="lg:col-span-4">
            <div className="sticky top-24 space-y-6">
              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-800 dark:to-slate-900 text-white border-0 shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Users className="w-5 h-5 text-blue-400" />
                    Komunitas & Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-slate-300 text-sm">
                    Bergabunglah dengan grup resmi untuk diskusi dan update info
                    real-time.
                  </p>

                  <div className="space-y-3">
                    <Button asChild className="w-full">
                      <Link to={web.linkInformasiSMBJM} target="_blank">
                        Informasi Lengkap SMBJM
                      </Link>
                    </Button>

                    {/* <Button
                      asChild
                      variant="outline"
                      className="w-full border-slate-600 bg-transparent hover:bg-slate-700 text-white hover:text-white dark:hover:bg-slate-700"
                    >
                      <Link to={web.linkOfficialGrupSMBJM} target="_blank">
                        Gabung Grup Telegram
                      </Link>
                    </Button> */}
                  </div>
                </CardContent>
              </Card>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-xl p-4">
                <p className="text-xs text-blue-800 dark:text-blue-300 text-center font-medium">
                  * Jadwal dapat berubah sewaktu-waktu sesuai kebijakan panitia
                  pusat/lokal.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
